import {
  ChronoUnit,
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  ZoneId,
  ZonedDateTime,
  nativeJs,
} from 'js-joda';
import { FormatDateOptions, IntlShape } from 'react-intl';
import { includes, indexOf, isEmpty, map } from 'es-toolkit/compat';

import { DisplayWeekday } from '@tock/time';

/**
 * Returns the list of all English weekday strings from SUNDAY to SATURDAY. This method
 * should not be used for any text to display to the user as it is not for i18n.
 * @ returns {array.<string>} the weekdays as English Strings.
 */
export function allWeekdayStrings(): Weekday[] {
  return ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
}

/**
 * Get a Weekday based on a dayIndex
 * For JS dates this is 0[Sunday] - 6[Saturday]
 * For JS-Joda day of week uses ISO-8601 so 1[Monday] - 7[Sunday]
 */
export function getWeekdayStringByIndex(dayIndex: number): Weekday {
  if (dayIndex > 7 || dayIndex < 0) {
    throw new Error(`Invalid weekday index. received: ${dayIndex}`);
  }
  const indexToGet = dayIndex === 7 ? 0 : dayIndex;
  return allWeekdayStrings()[indexToGet];
}

export const dayMap: { [weekday: string]: number } = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

const dateTimeFormatTest = /T\d\d:\d\d/;

/**
 * Returns the weekday number of the given weekday string.
 */
export function weekdayNumber(weekdayStringOrNumber: Weekday | number): number {
  if (typeof weekdayStringOrNumber === 'number') {
    return weekdayStringOrNumber;
  }
  return dayMap[weekdayStringOrNumber];
}

/**
 * Returns the weekday number of the given weekday string.
 */
export function toProtoWeekdayNumber(date: LocalDate): number {
  const localDayOfWeekVal = date.dayOfWeek().value();
  return localDayOfWeekVal === 7 ? 0 : localDayOfWeekVal;
}

export function weekdayOptions(): Array<{
  key: string;
  label: JSX.Element;
  value: Weekday;
}> {
  const weekdayStrings = allWeekdayStrings();

  return map(weekdayStrings, (weekdayString) => {
    const weekdayValue = indexOf(weekdayStrings, weekdayString);

    return {
      key: weekdayString,
      label: <DisplayWeekday displayWeekdayAs="short" weekdayValue={weekdayValue} />,
      value: weekdayString,
    };
  });
}

export function getIsSelectedWeekday(date: Date, selectedWeekdays: string[]): boolean {
  if (isEmpty(selectedWeekdays)) {
    return false;
  }

  const weekday = LocalDate.from(nativeJs(date)).dayOfWeek().toString();
  return includes(selectedWeekdays, weekday);
}

export function todayBusinessDay(): LocalDate {
  // Use the globally configured timezone which is the business time-zone
  return LocalDateTime.now().minusHours(4).toLocalDate();
}

export function todayAsLocalDate(timeZone: string): LocalDate {
  return todayAsLocalDateTime(timeZone).toLocalDate();
}

export function todayAsLocalDateTime(timeZone: string): LocalDateTime {
  return toLocalDateTime(Instant.now(), timeZone);
}

export function toLocalJSDate(date: LocalDate): Date {
  return new Date(date.year(), date.monthValue() - 1, date.dayOfMonth());
}

export function toJSDate(date: LocalDate): Date {
  return new Date(Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth()));
}

function toJSDateFromDate(dateValue: string | LocalDate | LocalDateTime): Date {
  let localDate: LocalDate;
  if (typeof dateValue === 'string') {
    if (dateTimeFormatTest.test(dateValue)) {
      return toJSDateFromDateTime(LocalDateTime.parse(dateValue));
    }
    localDate = LocalDate.parse(dateValue);
  } else if (dateValue instanceof LocalDate) {
    localDate = dateValue;
  } else {
    return toJSDateFromDateTime(dateValue);
  }
  return new Date(Date.UTC(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth()));
}

function toJSDateFromTime(timeValue: string | LocalTime | LocalDateTime | Date): Date {
  let localTime: LocalTime;
  if (typeof timeValue === 'string') {
    localTime = dateTimeFormatTest.test(timeValue)
      ? LocalDateTime.parse(timeValue).toLocalTime()
      : LocalTime.parse(timeValue);
  } else if (timeValue instanceof LocalTime) {
    localTime = timeValue;
  } else if (timeValue instanceof Date) {
    return timeValue;
  } else {
    return toJSDateFromDateTime(timeValue);
  }
  return toJSDateFromDateTime(localTime.atDate(LocalDate.EPOCH_0));
}

function toJSDateFromDateTime(dateTime: LocalDateTime): Date {
  const date = dateTime.toLocalDate();
  const time = dateTime.toLocalTime();
  return new Date(
    Date.UTC(date.year(), date.monthValue() - 1, date.dayOfMonth(), time.hour(), time.minute())
  );
}

export function toInstant(localDateTime: LocalDateTime, timeZone: string) {
  return Instant.from(ZonedDateTime.of(localDateTime, ZoneId.of(timeZone)));
}

export function toLocalDateTime(instant: Instant, timeZone: string) {
  return LocalDateTime.ofInstant(instant, ZoneId.of(timeZone));
}

export function toDateStringForDailyUrl(date: LocalDate): string {
  return date.toString();
}

/**
 * @param dateValue When dateValue is a string without an explicit timezone (ie, "2020-01-08T04:30"),
 * as is common with our internal representations, the time is not adjusted to any particular zone prior to formatting.
 */
export function formatDate(
  intl: IntlShape,
  dateValue: string | LocalDate | LocalDateTime,
  formatOptions?: FormatDateOptions | undefined
) {
  const jsDate = toJSDateFromDate(dateValue);
  return intl.formatDate(jsDate, formatOptions);
}

/**
 * @param timeValue When timeValue is a string without an explicit timezone (ie, "2020-01-08T04:30"),
 * as is common with our internal representations, the time is not adjusted to any particular zone prior to formatting.
 * When it is a Date, it is not modified.
 */
export function formatTime(
  intl: IntlShape,
  timeValue: string | LocalTime | LocalDateTime | Date,
  formatOptions?: FormatDateOptions | undefined
) {
  const jsDate = toJSDateFromTime(timeValue);
  return intl.formatTime(jsDate, formatOptions);
}

/**
 * Calculates the number of days (represented as a whole number) between two dates
 * @param start start date
 * @param end end date
 * @returns number of days in between the start date and end date
 */
export function numberOfDaysBetween(
  start: string | LocalDate | Date,
  end: string | LocalDate | Date
): number {
  const startDate = start instanceof Date ? start : toJSDateFromDate(start);
  const endDate = end instanceof Date ? end : toJSDateFromDate(end);

  return LocalDate.from(nativeJs(startDate)).until(nativeJs(endDate), ChronoUnit.DAYS);
}

export function compareLocalDate(a: LocalDate, b: LocalDate) {
  return a.compareTo(b);
}
